import { all, takeEvery, takeLatest } from '@redux-saga/core/effects';
import { GameHintStage } from 'enums/GameHintStage';
import { GameMenuState } from 'enums/GameMenuState';
import { OverlayType } from 'enums/OverlayType';
import { StorageKey } from 'enums/StorageKey';
import { DefaultRootState } from 'react-redux';
import { Action } from 'redux';
import { put, select } from 'redux-saga/effects';
import { services } from 'services/container';
import { selectIsExecutable } from 'state/connection/connection.selectors';
import { ChatStateController } from 'utils/chatState';
import { widgetStore } from 'utils/widgetStore';

import { setChatOverlay } from '../chat/chat.actions';
import { GameActionType, OpenGameWidgetAction, setGameMenuState } from './game.actions';

const queuedActions = [] as Action<GameActionType>[];

const manuallyStartGame = () => {
    import(/* webpackChunkName: "gameCoinGame" */ 'game/CoinGame').then((result) => {
        setTimeout(() => {
            const CoinGame = result.CoinGame;
            CoinGame.instance.setStore({
                dispatch: widgetStore.store.dispatch,
                getState: widgetStore.store.getState,
            });

            CoinGame.instance.start();
        }, 250);
    });
};

function* openGameWidgetSaga() {
    yield takeEvery(GameActionType.OpenWidget, function* (action: OpenGameWidgetAction) {
        const isInitiatedByDeepLink = (action as OpenGameWidgetAction).payload;

        if (isInitiatedByDeepLink) {
            const state: DefaultRootState = yield select();

            if (!state.chat.open) {
                ChatStateController.toggleChat(true);
            }

            const isExecutable = selectIsExecutable(state);

            if (!isExecutable) {
                queuedActions.push(action);
                return;
            }
        }

        yield put(setChatOverlay(OverlayType.CoinGame));
        yield put(setGameMenuState(GameMenuState.None));

        manuallyStartGame();
    });
}

function* nextHintStageSaga() {
    yield takeEvery(GameActionType.NextHint, function* () {
        const hintStage: DefaultRootState['game']['hintStage'] = yield select(
            (state: DefaultRootState) => state.game.hintStage,
        );
        localStorage.setItem(
            StorageKey.GameHintsFinished,
            JSON.stringify(hintStage === GameHintStage.Finished),
        );
    });
}

function* flushQueuedGameActionsSaga() {
    yield takeLatest(GameActionType.FlushQueuedGameActions, function* () {
        try {
            for (const action of queuedActions) {
                yield put(action);
            }
        } catch (e) {
            services.log.error('Error during queued game actions flushing: ' + String(e));
        }

        queuedActions.length = 0;
    });
}

export function* gameSaga() {
    yield all([openGameWidgetSaga(), nextHintStageSaga(), flushQueuedGameActionsSaga()]);
}
